<template>
    <div
        class="numeric-card numeric-goal-selector"
        :class="{
            'numeric-card--selected': isSelected,
            'numeric-card--warning': completion.current.waiting,
            'numeric-card--success': completion.current.success,
            'numeric-card--archived': !!numeric.archivedAt,
        }"
        :numeric-id="numeric.id"
        @contextmenu="openContextMenu($event, numeric)"
    >
        <div class="numeric-card__block numeric-card__block--title" @click="isCollapsed = !isCollapsed">
            <span class="numeric-card__title-overlay numeric-card__title-overlay--icon">
                <component class="svg svg--150" :is="completion.current.icon"></component>

                {{ numeric.year }}
            </span>

            <p
                class="numeric-card__title"
                :class="{
                    'numeric-card__title--blurry': !numeric.name,
                    'numeric-card__title--negative': !!numeric.archivedAt,
                }"
            >
                {{ numeric.name ? numeric.name : t('untitled') }}
            </p>
        </div>

        <div class="numeric-card__block numeric-card__block--status" v-if="!!numeric.archivedAt">
            <VTaskStatus :task="numeric"></VTaskStatus>
        </div>

        <div class="numeric-card__block numeric-card__block--completion">
            <div class="numeric-card__assigned">
                <VUserCard :user="numeric.assigneeId" v-if="numeric.assigneeId"></VUserCard>
            </div>

            <div class="numeric-card__completion">
                <VTitledValue v-bind="completion.quarter1"></VTitledValue>

                <VTitledValue v-bind="completion.quarter2"></VTitledValue>

                <VTitledValue v-bind="completion.quarter3"></VTitledValue>

                <VTitledValue v-bind="completion.quarter4"></VTitledValue>
            </div>
        </div>
    </div>
</template>

<script setup lang="ts">
// Svg
// ...

// Components
import VUserCard from './VUserCard.vue';
import VTitledValue from './VTitledValue.vue';

// Other
import { defineProps, PropType, ref } from 'vue';
import INumeric from '@/core/Models/INumeric';
import { useNumericCompletion, useNumericCard, useNumericContextMenu } from '@/mixins/NumericApi';
import { toRefs } from 'vue';
import { useI18n } from 'vue-i18n';
import VTaskStatus from '@/components/VTaskStatus.vue';

const { t } = useI18n();
const props = defineProps({
    goal: { type: Object as PropType<INumeric>, required: true },
});
const { goal: numeric } = toRefs(props);
const { isSelected } = useNumericCard(numeric);
const { completion } = useNumericCompletion(numeric);
const { openContextMenu } = useNumericContextMenu(numeric);

let isCollapsed = ref(true);
</script>

<style lang="scss">
.numeric-card {
    display: flex;
    flex: 1 1 24rem;
    flex-wrap: wrap;
    justify-content: space-between;
    padding: 0.75rem 1.5rem;
    border-radius: 1.5rem;
    color: var(--text-black-tertiary);
    border: 0.0625rem solid transparent;
    background: var(--background);
    --background: var(--background-primary);
    overflow: hidden;

    &--success {
        color: var(--brand-green);

        background: var(--background-color);
        --background-color: var(--background-green);
    }

    &--warning {
        color: var(--brand-orange);

        background: var(--background-color);
        --background-color: var(--background-red);
    }

    &--selected {
        border-color: var(--color-blue);
        background: var(--background-color);
        --background-color: var(--background-blue);
    }

    &__block {
        display: flex;
        gap: 2rem;

        &--title {
            position: relative;
            flex: 1 0;
            min-width: 24rem;
        }

        &--status {
            justify-content: center;
            display: flex;
            flex-direction: column;
            padding-left: 2.5rem;
        }

        &--completion {
            display: flex;
            flex-grow: 1;
            min-width: 24rem;
            flex-wrap: wrap;
            justify-content: center;
            padding: 0.75rem 2.5rem 0 2.5rem;
        }
    }

    &__title {
        padding-left: 2.875rem;
        flex-shrink: 0;
        width: 100%;
        margin: 1rem 0;
        line-height: 1.5rem;
        color: var(--text-black-primary);
        word-wrap: break-word;
        white-space: pre-wrap;

        @include h6();

        &--blurry {
            color: var(--text-black-tertiary);
        }

        &--negative {
            color: var(--color-negative);
        }
    }

    &__title-overlay {
        position: absolute;

        &--icon {
            display: flex;
            align-items: center;
            flex-direction: column;
            top: 1rem;
        }
    }

    &__completion {
        display: flex;
        min-width: 24rem;
    }

    &__completion-splitter {
        width: 0.0625rem;
        background-color: var(--background-quaternary);
    }

    &__assigned {
        display: flex;
        flex-shrink: 0;
        margin-top: 0.25rem;
        width: 18rem;
    }

    &__assigned,
    &__completion {
        height: 2.5rem;
    }
}
</style>
